import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FiSearch, FiHeart, FiUser, FiX, FiArrowRight } from 'react-icons/fi';
import { logout } from '../store/slices/authSlice';
import api from '../utils/api';
import { formatPrice } from '../utils/helpers';
import logo from '../assets/logo-script.png';

const NAV = [
  { label: 'New Arrivals', to: '/shop?newArrivals=true', match: (l) => l.search.includes('newArrivals') },
  { label: 'Fabrics to Silhouette', to: '/fabrics-to-silhouette', match: (l) => l.pathname === '/fabrics-to-silhouette' },
  { label: 'Dresses', to: '/shop?productType=dress', match: (l) => l.search.includes('productType=dress') },
  { label: 'Seasonal Edit', to: '/shop?onSale=true', match: (l) => l.search.includes('onSale') },
  { label: 'About', to: '/about', match: (l) => l.pathname === '/about' },
  { label: 'Journal', to: '/journal', match: (l) => l.pathname.startsWith('/journal') },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { totals = {} } = useSelector((state) => state.cart || {});
  const wishlistCount = useSelector((state) => {
    const w = state.wishlist || {};
    return w.count ?? w.items?.length ?? 0;
  });
  const cartCount = totals.itemCount ?? 0;

  // Slide the bar away on the way down, bring it back on the way up
  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 90) setHidden(false);
      else if (y > last + 6) setHidden(true);
      else if (y < last - 6) setHidden(false);
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close overlays on route change
  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  // Lock the page and support Escape while the mobile menu is open
  useEffect(() => {
    if (!menuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Search: focus, click-outside, escape
  useEffect(() => {
    if (!searchOpen) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    const onPointer = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target) && !e.target.closest('[data-search-toggle]')) {
        setSearchOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setSearchOpen(false); };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [searchOpen]);

  // Debounced live search
  useEffect(() => {
    const q = query.trim();
    if (!searchOpen || q.length < 2) {
      setResults([]);
      setSearchError(null);
      return undefined;
    }
    let active = true;
    const id = setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      try {
        const res = await api.get('/products', { params: { search: q, limit: 6 } });
        if (active) setResults(res.data?.data?.products || []);
      } catch {
        if (active) { setResults([]); setSearchError('Unable to search right now.'); }
      } finally {
        if (active) setSearching(false);
      }
    }, 280);
    return () => { active = false; clearTimeout(id); };
  }, [query, searchOpen]);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/shop?search=${encodeURIComponent(q)}`);
    setQuery('');
    setSearchOpen(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    setMenuOpen(false);
    navigate('/');
  };

  const toggleSearch = () => {
    setMenuOpen(false);
    setSearchOpen((v) => !v);
  };

  const firstName = user?.name?.split(' ')[0];

  return (
    <header className={`d-header ${hidden && !menuOpen && !searchOpen ? 'is-hidden' : ''}`}>
      <Link to="/" className="d-brand" aria-label="The Tamarind Leaf — home">
        <img src={logo} alt="The Tamarind Leaf" className="d-brand__logo" />
      </Link>

      <button
        type="button"
        className={`d-toggle ${menuOpen ? 'is-open' : ''}`}
        onClick={() => setMenuOpen((v) => !v)}
        aria-expanded={menuOpen}
        aria-controls="primary-navigation"
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
      >
        <span className="d-toggle__bars" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </button>

      <span className="d-icons">
        <button type="button" className="tl-icon-btn" onClick={toggleSearch} aria-label="Search" data-search-toggle>
          {searchOpen ? <FiX /> : <FiSearch />}
        </button>
        <Link to="/wishlist" className="tl-icon-btn" aria-label="Wishlist">
          <FiHeart />
          {wishlistCount > 0 && <span className="tl-badge-count">{wishlistCount}</span>}
        </Link>
        {isAuthenticated ? (
          <Link to="/profile" className="tl-icon-btn" aria-label={`Account${firstName ? ` — ${firstName}` : ''}`}><FiUser /></Link>
        ) : (
          <Link to="/login" className="tl-icon-btn" aria-label="Sign in"><FiUser /></Link>
        )}
        {isAuthenticated && <button type="button" className="tl-icon-btn" onClick={handleLogout} aria-label="Sign out" style={{ fontSize: 10, fontWeight: 800, width: 'auto', padding: '0 6px' }}>OUT</button>}
      </span>

      <nav id="primary-navigation" className={`d-nav ${menuOpen ? 'is-open' : ''}`} aria-label="Primary">
        <div className="d-nav__top">
          <span className="d-nav__eyebrow">Menu</span>
          <button type="button" className="d-nav__close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <FiX />
          </button>
        </div>

        <div className="d-nav__links">
          {NAV.map((item, index) => (
            <NavLink key={item.label} to={item.to} end className={item.match(location) ? 'is-active' : ''} onClick={() => setMenuOpen(false)}>
              <span className="d-nav__num" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
          <Link to="/cart" className={location.pathname === '/cart' ? 'is-active' : ''} onClick={() => setMenuOpen(false)}>
            <span className="d-nav__num" aria-hidden="true">{String(NAV.length + 1).padStart(2, '0')}</span>
            <span>Cart{cartCount > 0 ? ` (${cartCount})` : ''}</span>
          </Link>
        </div>

        <div className="d-nav__foot">
          <span className="d-nav__eyebrow">Your account</span>
          <div className="d-nav__utilities">
            <button type="button" onClick={() => { setMenuOpen(false); setSearchOpen(true); }}>
              <FiSearch aria-hidden="true" /> Search
            </button>
            <Link to="/wishlist" onClick={() => setMenuOpen(false)}>
              <FiHeart aria-hidden="true" /> Wishlist{wishlistCount > 0 ? ` (${wishlistCount})` : ''}
            </Link>
            {isAuthenticated ? (
              <Link to="/profile" onClick={() => setMenuOpen(false)}>
                <FiUser aria-hidden="true" /> {firstName || 'Account'}
              </Link>
            ) : (
              <Link to="/login" onClick={() => setMenuOpen(false)}>
                <FiUser aria-hidden="true" /> Sign in
              </Link>
            )}
          </div>
          {isAuthenticated && (
            <button type="button" className="d-nav__signout" onClick={handleLogout}>Sign out</button>
          )}
        </div>
      </nav>

      <div className={`d-search ${searchOpen ? 'is-open' : ''}`} ref={searchRef} aria-hidden={!searchOpen}>
        <div className="tl-container">
            <form className="tl-search__form" onSubmit={submitSearch} role="search">
              <FiSearch className="h-5 w-5 flex-none text-olive" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search sarees, kurtas, jewellery…"
                aria-label="Search products"
              />
              <button type="submit" className="tl-link">Search <FiArrowRight /></button>
            </form>
            {(searching || searchError || results.length > 0 || query.trim().length > 1) && (
              <div className="tl-search__results">
                {searching ? (
                  <p className="tl-search__status">Searching…</p>
                ) : searchError ? (
                  <p className="tl-search__status">{searchError}</p>
                ) : results.length > 0 ? (
                  <ul className="grid gap-1 md:grid-cols-2">
                    {results.map((p) => {
                      const img = p.images?.find((i) => i.isPrimary)?.url || p.images?.[0]?.url;
                      return (
                        <li key={p._id}>
                          <Link to={`/product/${p.slug || p._id}`} className="tl-search__item" onClick={() => { setSearchOpen(false); setQuery(''); }}>
                            {img ? <img className="tl-search__thumb" src={img} alt="" /> : <span className="tl-search__thumb tl-media-placeholder" />}
                            <span>
                              <span className="tl-search__name block">{p.name}</span>
                              <span className="tl-search__meta">{p.category?.name}{p.category?.name ? ' · ' : ''}{formatPrice(p.price)}</span>
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="tl-search__status">No pieces found for “{query.trim()}”.</p>
                )}
              </div>
            )}
        </div>
      </div>
    </header>
  );
};

export default Header;
